/**
 * Billing Run List
 *
 * One run per period: per billing entity it issues the HandOff invoice (10% leg), the
 * self-billed invoice (8% leg) and the settlement that nets them, then delivers all three
 * by email — unless the run is HELD, in which case an admin releases it here.
 * Releasing IS issuing: it archives the PDFs, starts the due date and the objection window and
 * sends the first email. Settlements only RESEND what a release already sent.
 * Pilot-minimal (spec §9): preview, create (held by default), inspect, release, cancel.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  PlayArrow as RunIcon,
  Send as ReleaseIcon,
} from '@mui/icons-material';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

import { billingRunApi } from '../../services/invoicingApi';
import { billingEntityApi } from '../../services/api';
import usePageLoadingEffect from '../../hooks/usePageLoadingEffect';

const statusColors = {
  pending: 'warning',
  running: 'info',
  completed: 'success',
  completed_with_issues: 'warning',
  failed: 'error',
  cancelled: 'default',
  skipped: 'default',
  pdf_failed: 'error',
};

const statusLabels = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  completed_with_issues: 'Completed with Issues',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const money = (v) => `EUR ${Number(v || 0).toFixed(2)}`;

const netLabel = (direction) => {
  if (direction === 'handoff_to_retailer') return 'HandOff pays';
  if (direction === 'retailer_to_handoff') return 'retailer pays';
  return 'balanced';
};

export default function InvoiceRunList() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  usePageLoadingEffect(loading);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [releasing, setReleasing] = useState(null);

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [periodStart, setPeriodStart] = useState(() => format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [periodEnd, setPeriodEnd] = useState(() => format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));

  // Test mode uses the _test tables; every test-mode email goes to the internal inbox.
  const [testMode, setTestMode] = useState(true);
  // Held by default: documents and emails are prepared, nothing is sent until Release.
  const [holdDelivery, setHoldDelivery] = useState(true);

  const [selectedEntities, setSelectedEntities] = useState([]);
  const [availableEntities, setAvailableEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await billingRunApi.list({ limit: rowsPerPage, offset: page * rowsPerPage, test_mode: testMode });
      setRuns(result.runs || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, testMode]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    if (createDialogOpen && availableEntities.length === 0) {
      setEntitiesLoading(true);
      billingEntityApi.list()
        .then((data) => setAvailableEntities(data?.entities || []))
        .catch((err) => console.error('Failed to fetch billing entities:', err))
        .finally(() => setEntitiesLoading(false));
    }
  }, [createDialogOpen, availableEntities.length]);

  const getTargetEntityIds = () => (selectedEntities.length ? selectedEntities.map((e) => e.id) : undefined);
  const entityLabel = (e) => `${e.legal_name}${e.storefronts?.length ? ` (${e.storefronts.join(', ')})` : ''}`;

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      setPreviewData(await billingRunApi.preview({
        supply_period_start: periodStart,
        supply_period_end: periodEnd,
        test_mode: testMode,
        target_billing_entity_ids: getTargetEntityIds(),
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreateRun = async () => {
    setCreating(true);
    try {
      const result = await billingRunApi.create({
        supply_period_start: periodStart,
        supply_period_end: periodEnd,
        test_mode: testMode,
        target_billing_entity_ids: getTargetEntityIds(),
        hold_delivery: holdDelivery,
      });
      setCreateDialogOpen(false);
      setPreviewData(null);
      setSelectedEntities([]);
      setNotice(
        `Run ${result.status}: ${result.successful_invoices} invoice(s), ${result.successful_self_bills} self-bill(s), ` +
        `${result.settlements_created} settlement(s)${holdDelivery ? ' — held, release to send' : ' — released'}`
      );
      fetchRuns();
    } catch (err) {
      setError(err.body?.message || err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRelease = async (run) => {
    const where = testMode ? 'the internal test inbox' : "each entity's billing email";
    const n = unsentCount(run);
    const message =
      `Issue and send ${n} statement${n === 1 ? '' : 's'} of run ${run.id.substring(0, 8)}?\n\n` +
      `This issues the invoices: the PDFs are archived, the payment term and objection window start, ` +
      `and the emails go to ${where}. Cancelled statements are skipped.`;
    if (!window.confirm(message)) return;
    setReleasing(run.id);
    try {
      const result = await billingRunApi.release(run.id, { test_mode: testMode });
      const failed = (result.failed || []).length;
      setNotice(`Released: ${(result.sent || []).length} sent, ${(result.already_sent || []).length} already sent, ${failed} failed`);
      if (failed) setError(result.failed.map((f) => `${f.settlement_id.substring(0, 8)}: ${f.error}`).join(' | '));
      fetchRuns();
    } catch (err) {
      setError(err.body?.message || err.message);
    } finally {
      setReleasing(null);
    }
  };

  const handleCancel = async (runId) => {
    if (!window.confirm('Cancel this billing run?')) return;
    try {
      await billingRunApi.cancel(runId, { test_mode: testMode });
      fetchRuns();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewDetails = async (run) => {
    setSelectedRun(run);
    setDetailDialogOpen(true);
    setDetailLoading(true);
    try {
      setSelectedRun(await billingRunApi.get(run.id, { test_mode: testMode }));
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (d) => (d ? format(new Date(d), 'MMM d, yyyy HH:mm') : '-');
  const formatPeriod = (s, e) => (s && e ? `${format(new Date(s), 'MMM d')} – ${format(new Date(e), 'MMM d, yyyy')}` : '-');
  // From the run list (counts) or the run detail (items): statements that are not cancelled.
  const liveItems = (run) => (run.items || []).filter((i) => i.settlement_id && i.settlement_status !== 'cancelled');
  const liveCount = (run) => (run.items ? liveItems(run).length : run.live_statements ?? 0);
  const unsentCount = (run) => (run.items ? liveItems(run).filter((i) => !i.sent_at).length : run.unsent_statements ?? 0);
  // A run whose statements were all cancelled (re-issued in a later run) has nothing to issue.
  const allCancelled = (run) => (run.settlements_created || 0) > 0 && liveCount(run) === 0;
  const canRelease = (run) =>
    run.hold_delivery && !run.released_at && ['completed', 'completed_with_issues'].includes(run.status) && unsentCount(run) > 0;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Billing Runs</Typography>
          <FormControlLabel
            control={<Switch checked={testMode} onChange={(e) => setTestMode(e.target.checked)} color="warning" />}
            label={<Chip label={testMode ? 'TEST MODE' : 'PRODUCTION'} color={testMode ? 'warning' : 'error'} size="small" />}
          />
        </Box>
        <Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRuns} disabled={loading} sx={{ mr: 1 }}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
            New Billing Run
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Run</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Entities</TableCell>
              <TableCell align="right">Invoices</TableCell>
              <TableCell align="right">Self-bills</TableCell>
              <TableCell align="right">Net</TableCell>
              <TableCell>Delivery</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => (
              <TableRow key={run.id} hover>
                <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{run.id.substring(0, 8)}…</Typography></TableCell>
                <TableCell>{formatPeriod(run.supply_period_start, run.supply_period_end)}</TableCell>
                <TableCell><Chip label={statusLabels[run.status] || run.status} color={statusColors[run.status] || 'default'} size="small" /></TableCell>
                <TableCell align="right">{run.total_retailers || 0}</TableCell>
                <TableCell align="right">{run.successful_invoices || 0}</TableCell>
                <TableCell align="right">{run.successful_self_bills || 0}</TableCell>
                <TableCell align="right">
                  {money(run.total_net_amount)}
                  {run.failed_invoices > 0 && (
                    <Typography variant="caption" color="error.main" display="block">{run.failed_invoices} failed</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {run.released_at ? (
                    <Chip size="small" color="success" label={`released ${format(new Date(run.released_at), 'MMM d')}`} />
                  ) : allCancelled(run) ? (
                    <Tooltip title="Every statement in this run was cancelled; nothing to issue">
                      <Chip size="small" label="all statements cancelled" />
                    </Tooltip>
                  ) : run.hold_delivery ? (
                    <Chip size="small" color="warning" label="held" />
                  ) : (
                    <Chip size="small" label="auto" />
                  )}
                </TableCell>
                <TableCell>{formatDate(run.created_at)}</TableCell>
                <TableCell>
                  <Tooltip title="View details">
                    <IconButton size="small" onClick={() => handleViewDetails(run)}><ViewIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {canRelease(run) && (
                    <Tooltip title={`Release: issue the invoices and send ${unsentCount(run)} statement email(s)`}>
                      <span>
                        <IconButton size="small" color="primary" disabled={releasing === run.id} onClick={() => handleRelease(run)}>
                          {releasing === run.id ? <CircularProgress size={16} /> : <ReleaseIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {(run.status === 'pending' || run.status === 'running') && (
                    <Tooltip title="Cancel run">
                      <IconButton size="small" color="error" onClick={() => handleCancel(run.id)}><CancelIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No billing runs found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      {/* Create dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Create Billing Run
          <Chip label={testMode ? 'TEST MODE' : 'PRODUCTION'} color={testMode ? 'warning' : 'error'} size="small" sx={{ ml: 2 }} />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {testMode ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Test mode:</strong> documents go to the _test tables, commission rows are not linked, and every email goes to the internal test inbox with a [TEST] subject.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Production:</strong> real invoices, self-bills and settlements. Keep “Hold delivery” on to review before anything is sent.
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Per billing entity the run issues the HandOff invoice (what its storefronts received), the self-billed invoice
              (what they sent) and one settlement statement. Pending commissions in either role block the entity.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
              <TextField label="Period Start" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Period End" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Box>

            <Autocomplete
              multiple
              options={availableEntities}
              getOptionLabel={entityLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedEntities}
              onChange={(event, v) => setSelectedEntities(v)}
              loading={entitiesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Billing Entities (optional)"
                  placeholder={selectedEntities.length === 0 ? 'Search and select entities…' : ''}
                  helperText="Leave blank to include every entity with approved commissions in either role."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (<>{entitiesLoading ? <CircularProgress size={20} /> : null}{params.InputProps.endAdornment}</>),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => <Chip label={option.legal_name} size="small" {...getTagProps({ index })} key={option.id} />)
              }
              filterSelectedOptions
              disableCloseOnSelect
              fullWidth
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={<Switch checked={holdDelivery} onChange={(e) => setHoldDelivery(e.target.checked)} />}
              label={holdDelivery ? 'Hold delivery — prepare documents and emails, send on Release' : 'Send emails immediately when the run completes'}
              sx={{ mb: 2 }}
            />

            <Box>
              <Button variant="outlined" onClick={handlePreview} disabled={previewLoading} startIcon={previewLoading ? <CircularProgress size={16} /> : <ViewIcon />}>
                Preview Run
              </Button>
            </Box>

            {previewData && previewData.can_proceed === false && (
              <Alert severity="error" sx={{ mt: 2 }}>
                A run is already in progress for this period ({previewData.conflict?.run_id?.substring(0, 8)}…). {previewData.suggestion}
              </Alert>
            )}

            {previewData && previewData.can_proceed !== false && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Preview</Typography>
                  <Typography variant="body2">
                    <strong>{previewData.summary?.total_entities || 0}</strong> billing entities —{' '}
                    HandOff → retailers: {previewData.summary?.total_handoff_commissions || 0} items, {money(previewData.summary?.total_handoff_amount)} excl. VAT;{' '}
                    retailers → HandOff: {previewData.summary?.total_self_bill_commissions || 0} items, {money(previewData.summary?.total_self_bill_amount)} excl. VAT
                    {previewData.summary?.entities_will_skip > 0 && (<> — <strong>{previewData.summary.entities_will_skip}</strong> will be skipped</>)}
                    {previewData.warnings?.length > 0 && (<><br /><span style={{ color: 'orange' }}>Warning: {previewData.warnings.join(', ')}</span></>)}
                  </Typography>
                </Alert>

                {previewData.entities?.length > 0 && (
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Billing Entity</TableCell>
                          <TableCell>Storefronts</TableCell>
                          <TableCell align="right">HandOff → retailer</TableCell>
                          <TableCell align="right">Retailer → HandOff</TableCell>
                          <TableCell align="right">Net excl. VAT</TableCell>
                          <TableCell>Outcome</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.entities.slice(0, 20).map((r) => (
                          <TableRow key={r.billing_entity_id || r.retailer_id}>
                            <TableCell>{r.legal_name || <em>No billing entity</em>}</TableCell>
                            <TableCell>{(r.storefronts || []).join(', ')}</TableCell>
                            <TableCell align="right">
                              {money(r.handoff_side?.amount)}
                              <Typography variant="caption" display="block" color="text.secondary">{r.handoff_side?.commission_count || 0} items</Typography>
                            </TableCell>
                            <TableCell align="right">
                              {money(r.self_bill_side?.amount)}
                              <Typography variant="caption" display="block" color="text.secondary">{r.self_bill_side?.commission_count || 0} items</Typography>
                            </TableCell>
                            <TableCell align="right">
                              {money(Math.abs(r.net_excl_vat || 0))}
                              <Typography variant="caption" display="block" color="text.secondary">{netLabel(r.net_direction_excl_vat)}</Typography>
                            </TableCell>
                            <TableCell>
                              {r.will_skip ? (
                                <Chip size="small" color="warning" label={`skip: ${r.skip_reason}`} />
                              ) : (
                                <Chip size="small" color="success" label={[r.handoff_side?.commission_count ? 'invoice' : null, r.self_bill_side?.commission_count ? 'self-bill' : null, 'settlement'].filter(Boolean).join(' + ')} />
                              )}
                              {r.warnings?.length > 0 && (
                                <Typography variant="caption" display="block" color="text.secondary">{r.warnings.join('; ')}</Typography>
                              )}
                              {r.pending_items?.length > 0 && (
                                <Typography variant="caption" display="block" color="warning.main">
                                  Pending: {r.pending_items.slice(0, 5).map((p) => `${p.id.slice(0, 8)} (${p.buying_retailer_id} ← ${p.selling_retailer_id})`).join(', ')}
                                  {r.pending_items.length > 5 ? ` +${r.pending_items.length - 5} more` : ''}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {previewData.entities.length > 20 && (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography variant="body2" color="text.secondary">…and {previewData.entities.length - 20} more entities</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateRun}
            disabled={creating || !previewData || previewData.can_proceed === false || !previewData.summary?.total_entities}
            startIcon={creating ? <CircularProgress size={16} /> : <RunIcon />}
          >
            {holdDelivery ? 'Start Run (held)' : 'Start Run and Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Billing Run Details
          {selectedRun && (
            <Chip label={statusLabels[selectedRun.status] || selectedRun.status} color={statusColors[selectedRun.status] || 'default'} size="small" sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : selectedRun ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Period:</strong> {formatPeriod(selectedRun.supply_period_start, selectedRun.supply_period_end)}<br />
                <strong>Created:</strong> {formatDate(selectedRun.created_at)} by {selectedRun.created_by || 'system'}<br />
                <strong>Delivery:</strong>{' '}
                {selectedRun.released_at
                  ? `released ${formatDate(selectedRun.released_at)} by ${selectedRun.released_by}`
                  : allCancelled(selectedRun)
                    ? 'not released — every statement was cancelled'
                    : selectedRun.hold_delivery
                      ? 'held — not issued or sent until Release'
                      : 'sent on completion'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                {[
                  ['Entities', selectedRun.total_retailers, 'grey.200', 'text.secondary'],
                  ['Invoices', selectedRun.successful_invoices, 'success.light', 'success.dark'],
                  ['Self-bills', selectedRun.successful_self_bills, 'success.light', 'success.dark'],
                  ['Settlements', selectedRun.settlements_created, 'success.light', 'success.dark'],
                  ['Failed', selectedRun.failed_invoices, 'error.light', 'error.dark'],
                  ['Skipped', selectedRun.skipped_retailers, 'grey.200', 'text.secondary'],
                ].map(([label, value, bg, color]) => (
                  <Paper key={label} sx={{ p: 2, flex: 1, minWidth: 110, textAlign: 'center', bgcolor: bg }}>
                    <Typography variant="h5" color={color}>{value || 0}</Typography>
                    <Typography variant="caption" color={color}>{label}</Typography>
                  </Paper>
                ))}
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                HandOff invoices {money(selectedRun.total_handoff_amount)} · self-bills {money(selectedRun.total_self_bill_amount)} · net {money(selectedRun.total_net_amount)} (positive = retailers pay HandOff)
              </Typography>

              {selectedRun.items?.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Billing Entity</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Self-bill</TableCell>
                        <TableCell>Statement</TableCell>
                        <TableCell align="right">Net</TableCell>
                        <TableCell>Sent</TableCell>
                        <TableCell>Reason / error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedRun.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            {item.legal_name || item.company_name || item.retailer_id}
                            {item.storefronts?.length > 0 && (
                              <Typography variant="caption" display="block" color="text.secondary">{item.storefronts.join(', ')}</Typography>
                            )}
                          </TableCell>
                          <TableCell><Chip label={item.status} color={statusColors[item.status] || 'default'} size="small" /></TableCell>
                          <TableCell>{item.invoice_number || '-'}</TableCell>
                          <TableCell>{item.self_bill_number || '-'}</TableCell>
                          <TableCell>{item.statement_number || '-'}</TableCell>
                          <TableCell align="right">
                            {item.net_amount != null ? money(Math.abs(item.net_amount)) : '-'}
                            {item.net_direction && (
                              <Typography variant="caption" display="block" color="text.secondary">{netLabel(item.net_direction)}</Typography>
                            )}
                          </TableCell>
                          <TableCell>{item.sent_at ? format(new Date(item.sent_at), 'MMM d HH:mm') : '-'}</TableCell>
                          <TableCell>
                            {item.skip_reason && <Chip size="small" label={item.skip_reason} sx={{ mr: 1 }} />}
                            <Typography variant="body2" color="error" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.error_message}>
                              {item.error_message || ''}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {selectedRun && canRelease(selectedRun) && (
            <Button variant="contained" startIcon={<ReleaseIcon />} onClick={() => { setDetailDialogOpen(false); handleRelease(selectedRun); }}>
              Issue and send
            </Button>
          )}
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
